/* eslint-disable react/jsx-filename-extension */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Framework from '@greenville/framework';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextareaAutosize,
  Typography
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import _ from 'lodash';
import * as constants from '../../../common/constants';
import utils from '../../../common/utils';

// Style objects
const styles = {
  loaderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    borderRadius: '4px'
  },
  loaderText: {
    marginTop: '16px',
    color: '#005d83',
    fontWeight: 500
  },
  dialogTitle: {
    fontSize: '20px',
    fontWeight: 'bold'
  },
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: (theme) => theme.palette.grey[500]
  },
  tabsContainer: {
    borderBottom: '1px solid #e0e0e0',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  tabIndicator: {
    backgroundColor: '#005d83',
    height: '3px'
  },
  tabActive: {
    fontWeight: 600,
    color: '#005d83',
    textTransform: 'none',
    fontSize: '16px',
    minWidth: '120px'
  },
  tabInactive: {
    fontWeight: 400,
    color: '#6a7070',
    textTransform: 'none',
    fontSize: '16px',
    minWidth: '120px'
  },
  dialogContent: {
    minHeight: '500px'
  },
  contentBox: {
    minHeight: '500px'
  },
  sectionHeading: {
    marginBottom: '15px',
    fontSize: '18px',
    fontWeight: 600
  },
  reasonsContainer: {
    marginBottom: '15px'
  },
  formControlLabel: {
    marginBottom: '2px',
    paddingTop: '0px',
    display: 'block'
  },
  textarea: {
    width: '100%',
    border: '1px solid #c4c4c4',
    borderRadius: '4px',
    padding: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    overflow: 'auto'
  },
  accordion: {
    marginBottom: '10px',
    minHeight: 'auto'
  },
  accordionSummary: {
    minHeight: '48px',
    height: '48px'
  },
  accordionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    paddingRight: '16px'
  },
  accordionLabel: {
    fontWeight: 600,
    fontSize: '14px'
  },
  commentLabel: {
    marginBottom: '10px'
  },
  commentText: {
    marginBottom: '15px',
    padding: '10px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  reasonsLabel: {
    marginBottom: '5px'
  },
  reasonChip: {
    display: 'inline-block',
    padding: '4px 8px',
    margin: '2px',
    backgroundColor: '#e3f2fd',
    borderRadius: '4px',
    fontSize: '12px'
  },
  noDataContainer: {
    textAlign: 'center',
    padding: '60px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px'
  },
  noDataHeading: {
    color: '#6a7070',
    marginBottom: '10px',
    fontWeight: 500
  },
  dialogActions: {
    padding: '16px 24px'
  }
};

const FeedbackModal = ({
  open,
  onClose,
  viewType,
  questionData,
  summaryData,
  questionArrayList,
  pageNumber,
  tocBookId,
  currentPageId,
  tenantId,
  tenantName,
  tenantConfig,
  chatgptTocUtility,
  chatgptSummaryUtility
}) => {
  const [feedbackTabValue, setFeedbackTabValue] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // Get feedback history from current data
  const getFeedbackHistory = () => {
    let feedbacks = [];
    if (viewType === 'quiz') {
      // Get the current question from the array to ensure we have latest data including feedbacks
      const currentQuestion = questionArrayList[pageNumber];
      feedbacks = currentQuestion?.feedbacks || questionData?.feedbacks || [];
    } else {
      feedbacks = summaryData?.feedbacks || [];
    }

    // Sort by updatedAt in descending order (newest first)
    return [...feedbacks].sort((a, b) => {
      // Parse timestamps - handle both epoch seconds and milliseconds
      let timeA = 0;
      let timeB = 0;

      if (a.updatedAt) {
        const numA = typeof a.updatedAt === 'string' ? parseInt(a.updatedAt, 10) : a.updatedAt;
        // If timestamp is in seconds (< year 3000 in milliseconds), convert to milliseconds
        timeA = numA < 10000000000 ? numA * 1000 : numA;
      }

      if (b.updatedAt) {
        const numB = typeof b.updatedAt === 'string' ? parseInt(b.updatedAt, 10) : b.updatedAt;
        // If timestamp is in seconds (< year 3000 in milliseconds), convert to milliseconds
        timeB = numB < 10000000000 ? numB * 1000 : numB;
      }

      return timeB - timeA; // Descending order (newest first)
    });
  };

  // Format date/time for feedback display
  const formatFeedbackDate = (dateValue) => {
    if (!dateValue) return 'N/A';

    let date;
    // Handle epoch timestamps (in milliseconds or seconds)
    if (typeof dateValue === 'number') {
      // If timestamp is in seconds (< year 3000 in milliseconds), convert to milliseconds
      date = dateValue < 10000000000 ? new Date(dateValue * 1000) : new Date(dateValue);
    } else if (typeof dateValue === 'string') {
      // Try parsing as number first (epoch as string)
      const numericValue = parseInt(dateValue, 10);
      if (!Number.isNaN(numericValue)) {
        date = numericValue < 10000000000 ? new Date(numericValue * 1000) : new Date(numericValue);
      } else {
        // Parse as ISO string or other date format
        date = new Date(dateValue);
      }
    } else {
      date = new Date(dateValue);
    }

    // Check if date is valid
    if (Number.isNaN(date.getTime())) return 'N/A';

    // Format as MM/DD/YYYY, HH:MM:SS
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackTabValue(0); // Reset to first tab
    // Reset form
    setFeedbackComment('');
    onClose();
  };

  // Listen for MCQ comment success and refresh data
  useEffect(() => {
    const handleMcqCommentSuccess = () => {
      if (viewType === 'quiz') {
        // Refresh question data to get updated feedbacks
        const params = {
          bookId: tocBookId,
          sourceId: currentPageId,
          tenantId: tenantId || utils.getTenantId(tenantName, tenantConfig)
        };
        chatgptTocUtility.fetch(params);

        // Reset form, hide loader, and close modal
        setFeedbackSubmitting(false);
        setFeedbackComment('');
        handleCloseFeedbackModal();
      }
    };

    const unsubscribeMcqComment = Framework.getEventManager().on(
      constants.SET_MCQ_COMMENT_SUCCESS,
      handleMcqCommentSuccess
    );

    return () => {
      unsubscribeMcqComment();
    };
  }, [viewType, tocBookId, currentPageId, tenantId, tenantName, chatgptTocUtility, tenantConfig]);

  // Listen for Summary comment success and refresh data
  useEffect(() => {
    const handleSummaryCommentSuccess = () => {
      if (viewType === 'summary') {
        // Refresh summary data to get updated feedbacks
        const summaryParams = {
          bookId: tocBookId,
          pageId: currentPageId,
          ...(!_.isEmpty(tenantId) && { tenantId })
        };
        chatgptSummaryUtility.fetch(summaryParams);

        // Reset form, hide loader, and close modal
        setFeedbackSubmitting(false);
        setFeedbackComment('');
        handleCloseFeedbackModal();
      }
    };

    const unsubscribeSummaryComment = Framework.getEventManager().on(
      constants.SET_SUMMARY_COMMENT_SUCCESS,
      handleSummaryCommentSuccess
    );

    return () => {
      unsubscribeSummaryComment();
    };
  }, [viewType, tocBookId, currentPageId, tenantId, chatgptSummaryUtility]);

  const handleFeedbackTabChange = (event, newValue) => {
    setFeedbackTabValue(newValue);
  };

  const handleFeedbackCommentChange = (event) => {
    setFeedbackComment(event.target.value);
  };

  const handleSubmitFeedback = () => {
    // Show loader
    setFeedbackSubmitting(true);

    // Prepare feedback data based on viewType
    let feedbackData;

    if (viewType === 'quiz') {
      feedbackData = {
        bookId: questionData?.bookId || tocBookId,
        questionId: questionData?.questionId,
        productId: questionData?.productId,
        version: questionData?.version,
        lifeCycleStatus: questionData?.lifeCycleStatus,
        comment: feedbackComment,
        tenantId
      };

      // Call API to submit feedback for quiz
      // The success event listener will handle refreshing data and closing modal
      chatgptTocUtility.addMcqComment(feedbackData);
    } else {
      // viewType === 'summary'
      feedbackData = {
        bookId: summaryData?.bookId || tocBookId,
        pageId: summaryData?.pageId,
        productId: summaryData?.productId,
        version: summaryData?.version,
        lifeCycleStatus: summaryData?.lifeCycleStatus,
        comment: feedbackComment,
        tenantId
      };

      // Call API to submit feedback for summary
      // The success event listener will handle refreshing data and closing modal
      chatgptSummaryUtility.addSummaryComment(feedbackData);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseFeedbackModal} maxWidth="md" fullWidth>
      {/* Loader Overlay */}
      {feedbackSubmitting && (
        <Box style={styles.loaderOverlay}>
          <Box style={{ textAlign: 'center' }}>
            <CircularProgress size={50} thickness={4} />
            <Typography variant="body1" style={styles.loaderText}>
              Submitting feedback...
            </Typography>
          </Box>
        </Box>
      )}

      <DialogTitle>
        <Typography variant="h5" style={styles.dialogTitle}>
          Feedback/Comment
        </Typography>
        <IconButton aria-label="close" onClick={handleCloseFeedbackModal} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={feedbackTabValue}
        onChange={handleFeedbackTabChange}
        aria-label="feedback tabs"
        TabIndicatorProps={{ style: styles.tabIndicator }}
        style={styles.tabsContainer}
      >
        <Tab label="Add Comment" style={feedbackTabValue === 0 ? styles.tabActive : styles.tabInactive} />
        <Tab label="Comment History" style={feedbackTabValue === 1 ? styles.tabActive : styles.tabInactive} />
      </Tabs>

      <DialogContent style={styles.dialogContent}>
        {/* Tab 1: Add Comment */}
        {feedbackTabValue === 0 && (
          <Box style={styles.contentBox}>
            <Typography variant="h6" style={styles.sectionHeading}>
              What was wrong with the Content?
            </Typography>
            <TextareaAutosize
              minRows={5}
              maxRows={10}
              placeholder="Please explain..."
              value={feedbackComment}
              onChange={handleFeedbackCommentChange}
              style={styles.textarea}
            />
          </Box>
        )}

        {/* Tab 2: Comment History */}
        {feedbackTabValue === 1 && (
          <Box style={styles.contentBox}>
            {getFeedbackHistory().length > 0 ? (
              getFeedbackHistory().map((feedback, index) => (
                <Accordion key={index} style={styles.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${index}-content`}
                    style={styles.accordionSummary}
                  >
                    <Box style={styles.accordionHeader}>
                      <Box>
                        <Typography variant="body2" style={styles.accordionLabel}>
                          <strong>Added By:</strong> {feedback.addedBy}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" style={styles.accordionLabel}>
                          <strong>Updated At:</strong> {formatFeedbackDate(feedback.updatedAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box style={{ width: '100%' }}>
                      <Typography variant="body2" style={styles.commentLabel}>
                        <strong>Comment:</strong>
                      </Typography>
                      <Typography variant="body2" style={styles.commentText}>
                        {feedback.comment}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <Box style={styles.noDataContainer}>
                <Typography variant="h6" style={styles.noDataHeading}>
                  No Feedback Available
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  This {viewType === 'quiz' ? 'question' : 'summary'} does not have any feedback comments yet.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions style={styles.dialogActions}>
        <Button onClick={handleCloseFeedbackModal} color="primary" variant="outlined" size="small">
          Cancel
        </Button>
        {feedbackTabValue === 0 && (
          <Button
            onClick={handleSubmitFeedback}
            color="primary"
            variant="outlined"
            size="small"
            disabled={!feedbackComment || feedbackComment.trim().length === 0}
          >
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

FeedbackModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  questionData: PropTypes.object,
  summaryData: PropTypes.object,
  questionArrayList: PropTypes.array,
  pageNumber: PropTypes.number,
  tocBookId: PropTypes.string.isRequired,
  currentPageId: PropTypes.string,
  tenantId: PropTypes.string,
  tenantName: PropTypes.string,
  tenantConfig: PropTypes.object,
  chatgptTocUtility: PropTypes.object.isRequired,
  chatgptSummaryUtility: PropTypes.object.isRequired
};

FeedbackModal.defaultProps = {
  questionData: {},
  summaryData: {},
  questionArrayList: [],
  pageNumber: 0,
  currentPageId: '',
  tenantId: '',
  tenantName: '',
  tenantConfig: {}
};

export default FeedbackModal;
